import { useState } from "react";
import { toast } from "react-toastify";
import { validateThresholdValue, buildThresholdsMap, formatCurrency, parseCurrencyAmount } from "../utils/utils";
import { updateThresholds } from "../api/threshold";

/**
 * Custom hook for managing threshold editing state and operations
 * @param {Array} accounts - Array of account objects
 * @param {Function} onThresholdUpdate - Callback function after successful threshold update
 * @returns {Object} - Threshold editing state and handlers
 */
export const useThresholdEditor = (accounts, onThresholdUpdate) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  /**
   * Start editing threshold for a specific account
   */
  const startEditing = (index, currentThreshold) => {
    setEditingIndex(index);
    const numericValue = parseCurrencyAmount(currentThreshold);
    setEditValue(numericValue.toString());
  };

  /**
   * Cancel editing without saving
   */
  const cancelEditing = () => {
    setEditingIndex(null);
    setEditValue("");
  };

  /**
   * Update edit value
   */
  const updateEditValue = (value) => {
    setEditValue(value);
  };

  /**
   * Save the edited threshold value
   */
  const saveThreshold = async (account) => {
    setSaving(true);

    try {
      // Validate the input value
      const validation = validateThresholdValue(editValue);

      if (!validation.valid) {
        toast.error(validation.error);
        setSaving(false);
        return false;
      }

      // Build the thresholds map with all currencies
      const thresholdsMap = buildThresholdsMap(
        accounts,
        account.currency,
        validation.numericValue
      );

      // Call API to update thresholds
      await updateThresholds(thresholdsMap);

      toast.success(
        `Threshold for ${account.currency} updated successfully to ${formatCurrency(validation.numericValue, account.currency)}`
      );

      // Reset editing state
      setEditingIndex(null);
      setEditValue("");

      // Notify parent to refresh data
      if (onThresholdUpdate) {
        onThresholdUpdate();
      }

      return true;
    } catch (err) {
      console.error("Error updating threshold:", err);
      toast.error(err.message || "Failed to update threshold");
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    editingIndex,
    editValue,
    saving,
    startEditing,
    cancelEditing,
    updateEditValue,
    saveThreshold,
  };
};
